/* Landing-page waitlist form (#291).
 *
 * POSTs the email same-origin to the console API's public intake route
 * (CloudFront /api/* → API Gateway POST /waitlist). The confirmation line
 * appears ONLY after a 200 whose body is JSON `{ ok: true }`. Everything else
 * is a failure — including a 200 carrying the landing page's HTML, which is
 * how CloudFront's distribution-level error pages mask an upstream 403/404 on
 * /api/* (#224). On failure the email stays in the field and the visitor can
 * retry.
 *
 * Kept free of top-level side effects so the dashboard test suite can import it;
 * landing.js wires it to the page. */

export const WAITLIST_ENDPOINT = '/api/waitlist';

/** Honeypot field name. Non-semantic ON PURPOSE: a name like `website` can be
 * autofilled for a real visitor, who would then be silently dropped. Must
 * match HONEYPOT_FIELD in infra/lib/lambdas/waitlist/handler.ts. */
export const HONEYPOT_FIELD = 'pt_hp';

const SUCCESS_TEXT = "◦ you're on the list — we'll reach out when your seat opens.";
const ERROR_TEXT = "Couldn't add you to the list just now. Please try again in a moment.";

async function postSignup(fetchImpl: typeof fetch, email: string, honeypot: string): Promise<boolean> {
  try {
    const res = await fetchImpl(WAITLIST_ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, [HONEYPOT_FIELD]: honeypot }),
    });
    if (res.status !== 200) return false;
    const body: unknown = await res.json(); // throws on HTML / non-JSON → failure
    return typeof body === 'object' && body !== null && (body as { ok?: unknown }).ok === true;
  } catch {
    return false;
  }
}

export function attachWaitlist(form: HTMLFormElement, fetchImpl: typeof fetch = fetch): void {
  const input = form.querySelector<HTMLInputElement>('input[type="email"]');
  const honeypot = form.querySelector<HTMLInputElement>(`input[name="${HONEYPOT_FIELD}"]`);
  const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  let inFlight = false;

  const clearError = () => form.parentElement?.querySelector('.pt-form-error')?.remove();
  const showError = () => {
    clearError();
    const err = document.createElement('p');
    err.className = 'pt-form-error';
    err.setAttribute('role', 'alert');
    err.textContent = ERROR_TEXT;
    form.after(err);
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (inFlight) return;
    const email = (input?.value ?? '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    inFlight = true;
    if (button) button.disabled = true;
    clearError();
    const ok = await postSignup(fetchImpl, email, honeypot?.value ?? '');
    inFlight = false;
    if (button) button.disabled = false;

    if (!ok) {
      showError();
      return;
    }
    const done = document.createElement('div');
    done.className = 'pt-form-success';
    done.textContent = SUCCESS_TEXT;
    form.parentElement?.querySelector('.pt-form-note')?.remove();
    form.replaceWith(done);
  });
}
